import { useEffect, useRef, useState, useCallback } from "react";

// ── Константы — должны совпадать с FlappyHost ─────────────────────────────────
const W = 400;
const H = 450;
const GRAVITY    = 0.38;
const FLAP_VEL   = -8;
const BIRD_R     = 16;
const PIPE_W     = 52;
const PIPE_INTERVAL = 90; // фреймов между трубами

export default function FlappyPlayer({ data, state, score, roundEnd, playerName, onDied, onPos }) {
  const canvasRef   = useRef(null);
  const gameRef     = useRef(null);
  const rafRef      = useRef(null);
  const lastPosRef  = useRef(0);

  const [phase, setPhase]       = useState("waiting");
  const [countdown, setCd]      = useState(3);
  const [localScore, setLocal]  = useState(0);
  const [pressed, setPressed]   = useState(false);

  // ── Тап ──────────────────────────────────────────────────────────────────────
  const handleTap = useCallback(() => {
    const g = gameRef.current;
    if (!g || g.phase !== "playing") return;
    g.bird.vy = FLAP_VEL;
    setPressed(true);
    setTimeout(() => setPressed(false), 100);
  }, []);

  // ── Старт раунда ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!data) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    gameRef.current = null;

    // A reconnect carries the last server-side snapshot. Continue from it instead
    // of showing a fake round-end screen or restarting the bird from zero.
    if (data.resume) {
      setPhase("playing");
      setLocal(Number(data.resume.pipes) || 0);
      const t = setTimeout(() => startGame(true), 0);
      return () => clearTimeout(t);
    }

    setPhase("countdown");
    setCd(3);
    setLocal(0);
    let c = 3;
    const iv = setInterval(() => {
      c--;
      setCd(c);
      if (c <= 0) { clearInterval(iv); startGame(false); }
    }, 1000);
    return () => { clearInterval(iv); };
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  function startGame(fromResume = false) {
    if (!canvasRef.current || !data) return;
    const resume = fromResume ? (data.resume || {}) : {};
    const frame = Math.max(0, Number(resume.frame) || 0);
    const birdX = Number.isFinite(Number(resume.x)) ? Number(resume.x) : 80;
    const birdY = Number.isFinite(Number(resume.y)) ? Number(resume.y) : H / 2;
    const birdVy = Number.isFinite(Number(resume.vy)) ? Number(resume.vy) : 0;
    const passed = Math.max(0, Number(resume.pipes) || 0);
    const visiblePipes = Array.isArray(resume.pipeData) ? resume.pipeData : [];

    gameRef.current = {
      phase: "playing",
      bird: { x: birdX, y: birdY, vy: birdVy, alive: true, pipes: passed },
      pipes: visiblePipes.map(p => ({
        x: Number(p.x) || 0,
        gapY: Number(p.gapY) || 100,
        scored: (Number(p.x) || 0) + PIPE_W < birdX,
      })),
      pipeIdx: Math.min((data.pipe_seed || []).length, Math.floor(frame / PIPE_INTERVAL)),
      pipeTimer: frame % PIPE_INTERVAL,
      frame,
      speed: data.speed || 3,
      gap: data.gap || 150,
      seed: data.pipe_seed || [],
    };
    setLocal(passed);
    setPhase("playing");
    rafRef.current = requestAnimationFrame(gameLoop);
  }

  function gameLoop(ts) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const g   = gameRef.current;
    if (!g || g.phase !== "playing") return;

    g.frame++;
    g.pipeTimer++;

    // Спавн трубы из seed
    if (g.pipeTimer >= PIPE_INTERVAL && g.pipeIdx < g.seed.length) {
      g.pipeTimer = 0;
      g.pipes.push({ x: W + PIPE_W, gapY: g.seed[g.pipeIdx], scored: false });
      g.pipeIdx++;
    }

    g.pipes.forEach(p => { p.x -= g.speed; });
    g.pipes = g.pipes.filter(p => p.x > -PIPE_W - 10);

    const bird = g.bird;
    bird.vy += GRAVITY;
    bird.y  += bird.vy;

    // Потолок / пол
    if (bird.y - BIRD_R < 0 || bird.y + BIRD_R > H) {
      killBird(g, ts); return;
    }

    // Коллизия с трубами
    for (const p of g.pipes) {
      const inX = bird.x + BIRD_R > p.x && bird.x - BIRD_R < p.x + PIPE_W;
      const inY = bird.y - BIRD_R < p.gapY || bird.y + BIRD_R > p.gapY + g.gap;
      if (inX && inY) { killBird(g, ts); return; }
      if (!p.scored && p.x + PIPE_W < bird.x) {
        p.scored = true;
        bird.pipes++;
        setLocal(bird.pipes);
      }
    }

    drawFrame(ctx, g);

    // Позиция → хост (throttle ~20fps)
    if (ts - lastPosRef.current > 50) {
      lastPosRef.current = ts;
      onPos && onPos({ x: bird.x, y: bird.y, vy: bird.vy, pipes: bird.pipes, alive: true, frame: g.frame, pipeData: g.pipes.map(p=>({x:Math.round(p.x),gapY:p.gapY})) });
    }

    rafRef.current = requestAnimationFrame(gameLoop);
  }

  function killBird(g, ts) {
    cancelAnimationFrame(rafRef.current);
    g.phase = "ended";
    g.bird.alive = false;
    const pipes = g.bird.pipes;
    setPhase("dead");
    setLocal(pipes);
    onPos && onPos({ x: g.bird.x, y: g.bird.y, vy: 0, pipes, alive: false, frame: g.frame });
    onDied && onDied(pipes);
  }

  function drawFrame(ctx, g) {
    const { bird, pipes, gap, frame } = g;

    // Небо
    ctx.fillStyle = "#87CEEB";
    ctx.fillRect(0, 0, W, H);

    // Облака
    ctx.fillStyle = "#ffffffcc";
    [[60,60,40,20],[200,90,55,25],[320,50,45,18]].forEach(([x,y,w,h]) => {
      ctx.beginPath(); ctx.ellipse(x,y,w,h,0,0,Math.PI*2); ctx.fill();
    });

    // Трубы
    pipes.forEach(p => {
      ctx.fillStyle = "#3AB54A";
      ctx.fillRect(p.x, 0, PIPE_W, p.gapY);
      ctx.fillStyle = "#2D8F3B";
      ctx.fillRect(p.x - 5, p.gapY - 22, PIPE_W + 10, 22);
      const botY = p.gapY + gap;
      ctx.fillStyle = "#3AB54A";
      ctx.fillRect(p.x, botY, PIPE_W, H - botY);
      ctx.fillStyle = "#2D8F3B";
      ctx.fillRect(p.x - 5, botY, PIPE_W + 10, 22);
    });

    // Земля
    ctx.fillStyle = "#8B6914"; ctx.fillRect(0, H - 20, W, 20);
    ctx.fillStyle = "#5D9E2B"; ctx.fillRect(0, H - 24, W, 6);

    // Птичка
    ctx.save();
    ctx.globalAlpha = bird.alive ? 1 : 0.4;
    ctx.translate(bird.x, bird.y);
    const angle = Math.max(-0.5, Math.min(0.8, bird.vy * 0.06));
    ctx.rotate(angle);

    ctx.fillStyle = "#6BCB77";
    ctx.beginPath(); ctx.ellipse(0, 0, BIRD_R+2, BIRD_R, 0, 0, Math.PI*2); ctx.fill();
    ctx.strokeStyle = "#000"; ctx.lineWidth = 2; ctx.stroke();

    ctx.fillStyle = "#fff";
    ctx.beginPath(); ctx.arc(5,-5,6,0,Math.PI*2); ctx.fill();
    ctx.fillStyle = "#111";
    ctx.beginPath(); ctx.arc(7,-5,3,0,Math.PI*2); ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.beginPath(); ctx.arc(8,-6,1,0,Math.PI*2); ctx.fill();

    ctx.fillStyle = "#FF8C00";
    ctx.beginPath(); ctx.moveTo(BIRD_R,-2); ctx.lineTo(BIRD_R+10,0); ctx.lineTo(BIRD_R,3); ctx.fill();

    const wingY = Math.sin(frame / 4) * 4;
    ctx.fillStyle = "#4aaa5a";
    ctx.beginPath(); ctx.ellipse(-3, wingY, 9, 5, -0.3, 0, Math.PI*2); ctx.fill();

    ctx.restore();

    // Счёт
    ctx.font = "bold 28px 'Fredoka One', sans-serif";
    ctx.textAlign = "center";
    ctx.strokeStyle = "#000"; ctx.lineWidth = 4;
    ctx.strokeText(`${bird.pipes}`, W/2, 44);
    ctx.fillStyle = "#ffe600";
    ctx.fillText(`${bird.pipes}`, W/2, 44);
  }

  // ── Round end ─────────────────────────────────────────────────────────────────
  if (state === "roundend" && roundEnd) {
    const myPipes = roundEnd.scores?.[playerName] ?? 0;
    const sorted  = Object.entries(roundEnd.scores || {}).sort((a,b)=>b[1]-a[1]);
    const myRank  = sorted.findIndex(([n])=>n===playerName) + 1;
    return (
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,padding:"1.5rem"}}>
        <div style={{fontSize:"4rem"}}>{["🥇","🥈","🥉"][myRank-1]||"🐦"}</div>
        <p style={{fontFamily:"'Fredoka One',sans-serif",fontSize:"1.8rem",color:myRank===1?"#ffe600":"#6BCB77",textShadow:"2px 2px 0 #000"}}>
          {myRank===1?"Ты лучший!":myRank===2?"Второе место!":myRank===3?"Третье место!":"Хороший полёт!"}
        </p>
        <div style={{background:"#1a1030",border:"2px solid #6BCB7755",borderRadius:"1rem",padding:"0.75rem 2rem",textAlign:"center"}}>
          <p style={{fontFamily:"'Nunito',sans-serif",color:"#aaa",fontSize:"0.9rem",marginBottom:4}}>Трубы пройдено</p>
          <p style={{fontFamily:"'Fredoka One',sans-serif",color:"#ffe600",fontSize:"2.5rem",lineHeight:1}}>{myPipes}</p>
          {roundEnd.survivor===playerName&&<p style={{fontFamily:"'Nunito',sans-serif",color:"#3dffa0",fontSize:"0.85rem",marginTop:6}}>🏆 Последний выживший +200!</p>}
        </div>
        <p style={{fontFamily:"'Nunito',sans-serif",color:"#555",fontSize:"0.85rem"}}>Следующий раунд скоро...</p>
      </div>
    );
  }

  // ── Dead ──────────────────────────────────────────────────────────────────────
  if (phase === "dead" || state === "dead") {
    return (
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12}}>
        <div style={{fontSize:"3.5rem"}}>💀</div>
        <p style={{fontFamily:"'Fredoka One',sans-serif",fontSize:"1.6rem",color:"#FF3355",textShadow:"2px 2px 0 #000"}}>Твоя птичка упала!</p>
        <div style={{background:"#1a1030",border:"2px solid #33333355",borderRadius:"1rem",padding:"0.65rem 1.5rem",textAlign:"center"}}>
          <p style={{fontFamily:"'Nunito',sans-serif",color:"#aaa",fontSize:"0.9rem"}}>Трубы пройдено</p>
          <p style={{fontFamily:"'Fredoka One',sans-serif",color:"#ffe600",fontSize:"2.5rem"}}>{localScore}</p>
        </div>
        <p style={{fontFamily:"'Nunito',sans-serif",color:"#444",fontSize:"0.85rem"}}>Наблюдай за остальными на экране хоста</p>
      </div>
    );
  }

  // ── Countdown ─────────────────────────────────────────────────────────────────
  if (phase === "countdown") {
    return (
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8}}>
        <p style={{fontFamily:"'Nunito',sans-serif",color:"#6BCB77",fontSize:"1rem"}}>Приготовься!</p>
        <div style={{fontFamily:"'Fredoka One',sans-serif",fontSize:"7rem",color:"#ffe600",textShadow:"4px 4px 0 #000",lineHeight:1}}>
          {countdown||"GO!"}
        </div>
        {/* Canvas скрыт но в DOM — чтобы canvasRef был доступен когда startGame вызовется */}
        <canvas ref={canvasRef} width={W} height={H} style={{display:"none"}}/>
      </div>
    );
  }

  // ── Playing ───────────────────────────────────────────────────────────────────
  return (
    <div
      style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4,userSelect:"none",WebkitUserSelect:"none"}}
      onTouchStart={(e)=>{e.preventDefault();handleTap();}}
    >
      <div style={{padding:"4px 0",fontFamily:"'Fredoka One',sans-serif",fontSize:"1.3rem",color:"#ffe600",textShadow:"2px 2px 0 #000"}}>
        🐦 {localScore}
      </div>
      <canvas
        ref={canvasRef}
        width={W} height={H}
        style={{
          width:"100%",maxWidth:W,borderRadius:"0.75rem",display:"block",
          border:`3px solid ${pressed?"#ffe600":"#6BCB77"}`,
          boxShadow:pressed?"0 0 16px #ffe60066":"0 0 8px #6BCB7744",
          transition:"border-color 0.1s,box-shadow 0.1s",
          touchAction:"none",
        }}
      />
      <p style={{fontFamily:"'Nunito',sans-serif",color:"#6BCB77",fontSize:"0.95rem",marginTop:4}}>
        Тапай по экрану чтобы лететь!
      </p>
    </div>
  );
}